import { DeleteForever, Publish, Save, Undo } from '@mui/icons-material';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration.js';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useDeleteVaxEntryMutation, usePublishVaxEntryMutation } from 'api/internal.api.ts';
import useRemoveFile from 'components/v1/impfschaeden/assistent/hooks/useRemoveFile.js';
import { Benutzerinformation } from 'components/v1/impfschaeden/assistent/VaxForm_02_Benutzerinformation/components/Benutzerinformation.js';
import { InfosZurPerson } from 'components/v1/impfschaeden/assistent/VaxForm_04_InfoZurPerson/components/InfosZurPerson.js';
import { isPlaceholderImage } from 'components/v1/impfschaeden/assistent/VaxForm_04_InfoZurPerson/components/PersonData.js';
import { VerbindungZurPerson } from 'components/v1/impfschaeden/assistent/VaxForm_05a_VerbindungZurPerson/components/VerbindungZurPerson.js';
import { WieErfahren } from 'components/v1/impfschaeden/assistent/VaxForm_06a_WieErfahren/components/WieErfahren.js';
import { Vorerkrankungen } from 'components/v1/impfschaeden/assistent/VaxForm_07_Vorerkrankungen/components/Vorerkrankungen.js';
import { ImpfschaedenForm } from 'components/v1/impfschaeden/assistent/VaxForm_08_Impfschaeden/components/ImpfschaedenForm.js';
import { ImpfungenForm } from 'components/v1/impfschaeden/assistent/VaxForm_09_Impfungen/components/ImpfungenForm.js';
import { initialVaccinationData } from 'components/v1/impfschaeden/assistent/VaxForm_09_Impfungen/VaxForm_09.js';
import { ProfilbildForm } from 'components/v1/impfschaeden/assistent/VaxForm_10_Profilbild/components/ProfilbildForm.js';
import { BilderVideosForm } from 'components/v1/impfschaeden/assistent/VaxForm_11_BilderVideos/components/BilderVideosForm.js';
import { BestaetigenForm } from 'components/v1/impfschaeden/assistent/VaxForm_12_Bestaetigen/components/BestaetigenForm.js';
import { DetailedViewModal } from 'components/v1/impfschaeden/uebersicht/EntryCard/EntryDetails.js';
import useUploadVaxEditFile from 'components/v1/nutzerbereich/impfschaden/hooks/useUploadVaxEditFile.js';
import { useNav } from 'components/v1/nutzerbereich/impfschaden/useNav.js';
import {
  ArrowBack,
  Button,
  Card,
  Col,
  Container,
  DropdownSelect,
  LoadingButton,
  Modal,
  Row,
  SpinnerIcon,
  SquareShadowCard,
  ThemeTitle
} from 'components/v1/ui';
import useRefreshToken from 'hooks/useRefreshToken.js';
import { availableLanguages, getStatusColor, getStatusText } from 'lib/utils';
import { vaxEditDataActions } from 'store/vaxEditDataSlice.js';

import useEditFormUpdate from './hooks/useEditFormUpdate.js';
dayjs.extend(duration);

const ImpfschadenBearbeiten = () => {
  useRefreshToken();
  // refs for scoll sync
  const step1Ref = useNav('step1link');
  const step1dot5Ref = useNav('step1dot5link');
  const step2Ref = useNav('step2link');
  const step3Ref = useNav('step3link');
  const step4Ref = useNav('step4link');
  const step5Ref = useNav('step5link');
  const step6Ref = useNav('step6link');
  const step7Ref = useNav('step7link');
  const step8Ref = useNav('step8link');
  const step9Ref = useNav('step9link');
  const step10Ref = useNav('step10link');
  const step0Ref = useNav('step0link');

  const dispatch = useDispatch();
  const router = useRouter();
  const { id } = router.query;
  const [t] = useTranslation();
  const vaxEditData = useSelector(state => state.vaxEditData);
  const {
    update: updateForm,
    updateUser,
    updateVaccination,
    fetchEntry,
    restoreData,
    saveEntry,
    isFetching,
    isLoading: isLoadingSave,
    isSuccess: isSuccessSave
  } = useEditFormUpdate();
  const { vaxManufacturers } = useSelector(state => state.masterData);
  const uploadVaxFile = useUploadVaxEditFile();
  const files = vaxEditData.vax_entry_files;
  const [publish, { isLoading: isLoadingPublish, isSuccess: isSuccessPublish }] = usePublishVaxEntryMutation();
  const [delete_entry, { isLoading: isLoadingDelete, isSuccess: isSuccessDelete }] = useDeleteVaxEntryMutation();
  const user = useSelector(state => state.settings.currentUser);

  const [showAdminRestriction, setShowAdminRestriction] = useState(false);
  const [showAdminWarning, setShowAdminWarning] = useState(false);

  const [newItems, setNewItems] = useState(false);

  const handleClose = () => {
    setShowAdminRestriction(false);
    setShowAdminWarning(false);
  };

  useEffect(() => {
    if (isSuccessSave == true) {
      dispatch(vaxEditDataActions.update({ edited: false }));
      fetchEntry({ id });
    }
  }, [isSuccessSave]);

  useEffect(() => {
    if (isSuccessPublish == true) {
      dispatch(vaxEditDataActions.update({ edited: false }));
      back();
    }
  }, [isSuccessPublish]);

  useEffect(() => {
    if (isSuccessDelete == true) {
      dispatch(vaxEditDataActions.update({ edited: false }));
      back();
    }
  }, [isSuccessDelete]);

  const hasNewVaccineTypeOrManufacturer = () => {
    let newVaxTypes = false;
    let newVaxManufacturers = false;
    for (let i = 0; i < vaxEditData.vax_entry_vaccinations.length; i++) {
      if (vaxEditData.vax_entry_vaccinations[i].vax_type.verified == 0) {
        newVaxTypes = true;
      }
      if (vaxEditData.vax_entry_vaccinations[i].vax_manufacturer.verified == 0) {
        newVaxManufacturers = true;
      }
    }
    if (newVaxTypes && newVaxManufacturers) {
      return 'both';
    } else if (newVaxTypes) {
      return 'newVaxTypes';
    } else if (newVaxManufacturers) {
      return 'newManufacturers';
    } else {
      return false;
    }
  };

  const acceptNewTypes = () => {
    publish({
      vax_entry_id: vaxEditData.vax_entry_id,
      published_by: user.email,
      add_vax: true
    });
  };

  const publishEntry = () => {
    let newItems = hasNewVaccineTypeOrManufacturer();
    if (newItems) {
      setNewItems(newItems);
      if (user?.is_admin) {
        setShowAdminWarning(true);
      } else {
        setShowAdminRestriction(true);
      }
    } else {
      publish({
        vax_entry_id: vaxEditData.vax_entry_id,
        published_by: user.email
      });
    }
    if (router?.query?.impfschaden != null) {
      let newQuery = { ...router.query };
      delete newQuery.id;
      delete newQuery.impfschaden;
      delete newQuery.lang;
      router.push({
        pathname: '/impfschaden',
        query: newQuery
      });
    } else {
      let newQuery = { ...router.query };
      delete newQuery.id;
      delete newQuery.impfschaden;
      delete newQuery.lang;
      // return;
      // router.push({
      // 	pathname: decodeURIComponent(router.asPath),
      // 	query: newQuery,
      // });
    }
  };

  const deleteEntry = () => {
    delete_entry({ vax_entry_id: vaxEditData.vax_entry_id });
  };

  const update = (data, reset) => {
    if (reset) {
      updateForm(data);
    } else {
      updateForm({ ...data, edited: true, glob_entry_status_id: 1 });
    }
  };
  const removeFile = useRemoveFile('internal');

  const anotherPerson = vaxEditData.own_enry;

  const getAgeDuration = (age, age_unit) => {
    const unit = age_unit == 'tag' ? 'd' : age_unit == 'monat' ? 'M' : 'y';
    return dayjs.duration(age, unit);
  };

  const getAgeGroupByAge = (age, age_unit) => {
    const duration = getAgeDuration(age, age_unit);
    const durationMonth = duration.asMonths();
    const durationYear = duration.asYears();
    if (durationMonth <= 1) {
      return 2;
    } else if (durationMonth <= 3) {
      return 3;
    } else if (durationMonth <= 4) {
      return 4;
    } else if (durationMonth <= 6) {
      return 5;
    } else if (durationYear <= 1) {
      return 6;
    } else if (durationYear < 3) {
      return 7;
    } else if (durationYear < 5) {
      return 8;
    } else if (durationYear < 7) {
      return 9;
    } else if (durationYear < 13) {
      return 10;
    } else if (durationYear < 19) {
      return 11;
    } else if (durationYear < 30) {
      return 12;
    } else if (durationYear < 40) {
      return 13;
    } else if (durationYear < 50) {
      return 14;
    } else if (durationYear < 60) {
      return 15;
    } else if (durationYear < 70) {
      return 16;
    } else if (durationYear < 80) {
      return 17;
    } else if (durationYear < 90) {
      return 18;
    } else {
      return 19;
    }
  };

  const setAvatarByAgeGroupId = ageGroupId => {
    switch (ageGroupId) {
      case 2:
      case 3:
      case 4:
      case 5:
      case 6:
      case 7:
        if (vaxEditData.sex == 'm') {
          update({ avatar_glob_file_id: 2 });
        } else {
          update({ avatar_glob_file_id: 3 });
        }
        break;
      case 8:
      case 9:
      case 10:
      case 11:
        if (vaxEditData.sex == 'm') {
          update({ avatar_glob_file_id: 4 });
        } else {
          update({ avatar_glob_file_id: 5 });
        }
        break;
      case 12:
      case 13:
      case 14:
      case 15:
        if (vaxEditData.sex == 'm') {
          update({ avatar_glob_file_id: 6 });
        } else {
          update({ avatar_glob_file_id: 7 });
        }
        break;
      case 16:
      case 17:
      case 18:
      case 19:
        if (vaxEditData.sex == 'm') {
          update({ avatar_glob_file_id: 8 });
        } else {
          update({ avatar_glob_file_id: 9 });
        }
        break;
    }
  };

  const editVaccination = (index, newData) => {
    let currentVaccinations = vaxEditData ? [...vaxEditData.vax_entry_vaccinations] : [];
    currentVaccinations[index] = {
      ...currentVaccinations[index],
      ...newData
    };

    // Alter wurde geändert
    if (newData.age || newData.age_unit || newData.glob_age_groups_id) {
      // Dann setze die Alters-Gruppe entsprechend der Angabe
      const age = newData.age || currentVaccinations[index].age;
      const age_unit = newData.age_unit || currentVaccinations[index].age_unit;
      let ageGroupId = newData.glob_age_groups_id || currentVaccinations[index].glob_age_groups_id;
      if (newData.age || newData.age_unit) {
        ageGroupId = getAgeGroupByAge(age, age_unit);
        currentVaccinations[index].glob_age_groups_id = ageGroupId;
      }
      // Falls kein FOto hochgeladen wurde ändere den Patzhalter Avatar
      if (index == 0 && isPlaceholderImage(vaxEditData.avatar_glob_file_id) && vaxEditData.sex != 'unknown') {
        setAvatarByAgeGroupId(parseInt(ageGroupId, 10));
      }
    }
    updateVaccination({
      vaccinations: currentVaccinations,
      trigger: Object.keys(newData)[0],
      index
    });
  };

  // const editVaccination = (index, newData) => {
  // 	let currentVaccinations = [...vaxEditData?.vax_entry_vaccinations];
  // 	currentVaccinations[index] = {
  // 		...currentVaccinations[index],
  // 		...newData,
  // 	};
  // 	updateVaccination({
  // 		vaccinations: currentVaccinations,
  // 		trigger: Object.keys(newData)[0],
  // 		index,
  // 	});
  // };

  const addVaccination = () => {
    let currentVaccinations = vaxEditData?.vax_entry_vaccinations ? [...vaxEditData.vax_entry_vaccinations] : [];
    currentVaccinations.push(initialVaccinationData);
    updateVaccination({
      vaccinations: currentVaccinations,
      trigger: null,
      index: vaxEditData.vax_entry_vaccinations.length
    });
  };

  const removeVaccination = index => {
    let currentVaccinations = vaxEditData ? [...vaxEditData.vax_entry_vaccinations] : [];
    currentVaccinations.splice(index, 1);
    updateVaccination({
      vaccinations: currentVaccinations,
      trigger: null,
      index
    });
  };

  const onChangeManufacturer = (newValue, index) => {
    if (typeof newValue === 'string') {
      const existing = vaxManufacturers.find(vaxManufacturer => {
        return vaxManufacturer.manufacturer_i18n_key === newValue;
      });
      if (existing) {
        editVaccination(index, {
          vax_manufacturer_id: existing.vax_manufacturer_id
        });
      } else {
        editVaccination(index, {
          vax_manufacturer_id: newValue
        });
      }
    }
  };

  useEffect(() => {
    if (id) {
      fetchEntry({ id });
    }
  }, [id]);

  const statusColor = useMemo(() => {
    return getStatusColor(vaxEditData.glob_entry_status_id);
  }, [vaxEditData.glob_entry_status_id]);

  const statusText = useMemo(() => {
    return getStatusText(vaxEditData.glob_entry_status_id);
  }, [vaxEditData.glob_entry_status_id]);

  const back = () => {
    if (router?.query?.impfschaden != null) {
      let newQuery = { ...router.query };
      delete newQuery.id;
      delete newQuery.impfschaden;
      delete newQuery.lang;
      router.push({
        pathname: '/impfschaden',
        query: newQuery
      });
    } else {
      router.push('/adminbereich/eintraege');
    }
  };
  // console.log('Impfschaden Bearbeiten', vaxEditData);
  if (!vaxEditData || !vaxEditData?.vax_entry_id) {
    return null;
  }

  if (isFetching) {
    return <SpinnerIcon />;
  }

  return (
    <>
      {/* <ThemeTitle text={getTitleText()} h={1} /> */}
      <div className="d-flex w-50">
        <ThemeTitle
          text={t('common.eintrag_bearbeiten')}
          h={5}
        />
        {!user?.is_admin && (
          <div>
            {t('common.sprache')}:&nbsp;{vaxEditData.lang_iso_code}
          </div>
        )}
        {user?.is_admin == 1 && (
          <div className="d-flex align-items-center mb-2">
            <div>{t('common.sprache')}:</div>
            <DropdownSelect
              style={{ width: '120px' }}
              aria-label={t('common.sprache')}
              onChange={e => {
                // console.log('update', e.target.value);
                update({
                  lang_iso_code: e.target.value,
                  edited: true
                });
              }}
              value={vaxEditData.lang_iso_code}
              options={availableLanguages.map(item => {
                return { value: item.lang_iso_code, text: item.lang_iso_code };
              })}
            />
          </div>
        )}
      </div>
      <div
        style={{
          boxShadow: '0px 0px 4px 1px ' + statusColor
        }}
      >
        <div style={{ position: 'relative' }}>
          <div
            className="d-flex flex-end position-absolute"
            style={{
              right: 0,
              zIndex: 1000,
              top: '-31px',
              color: 'white',
              background: statusColor,
              padding: '3px',
              borderTopLeftRadius: '10px',
              borderTopRightRadius: '10px'
            }}
          >
            {t('nutzerbereich.status')}: {t(statusText)}
          </div>
          <div
            id="step1anchor"
            style={{ position: 'absolute', top: '-200px' }}
          ></div>
          <div
            style={{ position: 'relative' }}
            ref={step0Ref}
          >
            <div
              id="step0anchor"
              style={{ position: 'absolute', top: '-200px' }}
            ></div>
            <SquareShadowCard className="mb-3">
              <Card.Body>
                <DetailedViewModal {...vaxEditData} />
              </Card.Body>
            </SquareShadowCard>
          </div>
          <div ref={step1Ref}></div>
          <SquareShadowCard className="mb-3">
            <Card.Title
              className="p-3"
              style={{ borderBottom: '1px solid #c4c4c4' }}
            >
              {t('nutzerbereich.eintraege.bearbeiten.benutzerinformation')}
            </Card.Title>
            <Card.Body>
              <Container className="p-0">
                <Benutzerinformation
                  anotherPerson={anotherPerson}
                  updateUser={updateUser}
                  validation={{}}
                  formData={vaxEditData}
                  hideCreateAccount={true}
                  edit={true}
                />
              </Container>
            </Card.Body>
          </SquareShadowCard>
        </div>
        <div
          style={{ position: 'relative' }}
          ref={step1dot5Ref}
        >
          <div
            id="step2anchor"
            style={{ position: 'absolute', top: '-200px' }}
          ></div>
          <SquareShadowCard className="mb-3">
            <Card.Title
              className="p-3"
              style={{ borderBottom: '1px solid #c4c4c4' }}
            >
              {t('master_data.referrer.eigener_eintrag')}
            </Card.Title>
            <Card.Body>
              <Container className="p-0">
                <DropdownSelect
                  style={{ width: '120px' }}
                  aria-label={t('master_data.referrer.eigener_eintrag')}
                  onChange={e => {
                    // console.log('update', e.target.value);
                    update({
                      own_entry: e.target.value,
                      edited: true
                    });
                  }}
                  value={vaxEditData.own_entry != null ? vaxEditData.own_entry.toString() : '0'}
                  options={[
                    { value: '1', text: t('common.ja') },
                    { value: '0', text: t('common.nein') }
                  ]}
                />
              </Container>
            </Card.Body>
          </SquareShadowCard>
        </div>
        <div
          style={{ position: 'relative' }}
          ref={step2Ref}
        >
          <div
            id="step2anchor"
            style={{ position: 'absolute', top: '-200px' }}
          ></div>
          <SquareShadowCard className="mb-3">
            <Card.Title
              className="p-3"
              style={{ borderBottom: '1px solid #c4c4c4' }}
            >
              {t('nutzerbereich.eintraege.bearbeiten.information_geimpfte_person')}
            </Card.Title>
            <Card.Body>
              <Container className="p-0">
                <InfosZurPerson
                  anotherPerson={anotherPerson}
                  formData={vaxEditData}
                  update={update}
                  validation={{}}
                />
              </Container>
            </Card.Body>
          </SquareShadowCard>
        </div>
        <div
          style={{ position: 'relative' }}
          ref={step3Ref}
        >
          <div
            id="step3anchor"
            style={{ position: 'absolute', top: '-200px' }}
          ></div>
          <SquareShadowCard className="mb-3">
            <Card.Title
              className="p-3"
              style={{ borderBottom: '1px solid #c4c4c4' }}
            >
              {t('nutzerbereich.eintraege.bearbeiten.verbindung_zur_person')}
            </Card.Title>
            <Card.Body>
              <Container className="p-0">
                <VerbindungZurPerson
                  update={update}
                  validation={{}}
                  formData={vaxEditData}
                />
              </Container>
            </Card.Body>
          </SquareShadowCard>
        </div>
        <div
          style={{ position: 'relative' }}
          ref={step4Ref}
        >
          <div
            id="step4anchor"
            style={{ position: 'absolute', top: '-200px' }}
          ></div>
          <SquareShadowCard className="mb-3">
            <Card.Title
              className="p-3"
              style={{ borderBottom: '1px solid #c4c4c4' }}
            >
              {t('common.herkunft_der_information')}
            </Card.Title>
            <Card.Body>
              <Container className="p-0">
                <WieErfahren
                  update={update}
                  validation={{}}
                  formData={vaxEditData}
                />
              </Container>
            </Card.Body>
          </SquareShadowCard>
        </div>
        <div
          style={{ position: 'relative' }}
          ref={step5Ref}
        >
          <div
            id="step5anchor"
            style={{ position: 'absolute', top: '-200px' }}
          ></div>
          <SquareShadowCard className="mb-3">
            <Card.Title
              className="p-3"
              style={{ borderBottom: '1px solid #c4c4c4' }}
            >
              {t('common.vorerkrankungen')}
            </Card.Title>
            <Card.Body>
              <Container className="p-0">
                <Row>
                  <Col>
                    {anotherPerson
                      ? t('impfschaeden.erfassen.geimpfter_gesund')
                      : t('impfschaeden.erfassen.vor_impfung_gesund')}
                  </Col>
                </Row>
                <Vorerkrankungen
                  anotherPerson={anotherPerson}
                  update={update}
                  validation={{}}
                  formData={vaxEditData}
                />
              </Container>
            </Card.Body>
          </SquareShadowCard>
        </div>
        <div
          style={{ position: 'relative' }}
          ref={step6Ref}
        >
          <div
            id="step6anchor"
            style={{ position: 'absolute', top: '-200px' }}
          ></div>
          <SquareShadowCard className="mb-3">
            <Card.Title
              className="p-3"
              style={{ borderBottom: '1px solid #c4c4c4' }}
            >
              {t('common.impfschaeden')}
            </Card.Title>
            <Card.Body>
              <Container className="p-0">
                <Row>
                  <ImpfschaedenForm
                    update={update}
                    validation={{}}
                    formData={vaxEditData}
                  />
                </Row>
              </Container>
            </Card.Body>
          </SquareShadowCard>
        </div>
        <div
          style={{ position: 'relative' }}
          ref={step7Ref}
        >
          <div
            id="step7anchor"
            style={{ position: 'absolute', top: '-200px' }}
          ></div>
          <SquareShadowCard className="mb-3">
            <Card.Title
              className="p-3"
              style={{ borderBottom: '1px solid #c4c4c4' }}
            >
              {t('nutzerbereich.eintraege.bearbeiten.impfungen')}
            </Card.Title>
            <Card.Body>
              <Container className="p-0">
                <Row>
                  <ImpfungenForm
                    addVaccination={addVaccination}
                    editVaccination={editVaccination}
                    removeVaccination={removeVaccination}
                    onChangeManufacturer={onChangeManufacturer}
                    formData={vaxEditData}
                    validation={{}}
                  />
                </Row>
              </Container>
            </Card.Body>
          </SquareShadowCard>
        </div>
        <div
          style={{ position: 'relative' }}
          ref={step8Ref}
        >
          <div
            id="step8anchor"
            style={{ position: 'absolute', top: '-200px' }}
          ></div>
          <SquareShadowCard className="mb-3">
            <Card.Title
              className="p-3"
              style={{ borderBottom: '1px solid #c4c4c4' }}
            >
              {t('nutzerbereich.eintraege.bearbeiten.profilbild')}
            </Card.Title>
            <Card.Body>
              <Container className="p-0">
                <Row>
                  <ProfilbildForm
                    formData={vaxEditData}
                    edit={true}
                  />
                </Row>
              </Container>
            </Card.Body>
          </SquareShadowCard>
        </div>
        <div
          style={{ position: 'relative' }}
          ref={step9Ref}
        >
          <div
            id="step9anchor"
            style={{ position: 'absolute', top: '-200px' }}
          ></div>
          <SquareShadowCard className="mb-3">
            <Card.Title
              className="p-3"
              style={{ borderBottom: '1px solid #c4c4c4' }}
            >
              {t('nutzerbereich.eintraege.bearbeiten.bilder_videos')}
            </Card.Title>
            <Card.Body>
              <Container className="p-0">
                <Row>
                  <BilderVideosForm
                    uploadVaxFile={uploadVaxFile}
                    files={files}
                    removeFile={removeFile}
                    changeFileOrder={vaxEditDataActions.changeFileOrder}
                    validation={{}}
                    update={update}
                  />
                </Row>
              </Container>
            </Card.Body>
          </SquareShadowCard>
        </div>
        <div
          style={{ position: 'relative' }}
          ref={step10Ref}
        >
          <div
            id="step10anchor"
            style={{ position: 'absolute', top: '-200px' }}
          ></div>
          <SquareShadowCard className="mb-3">
            <Card.Title
              className="p-3"
              style={{ borderBottom: '1px solid #c4c4c4' }}
            >
              {t('nutzerbereich.eintraege.bearbeiten.daten_freigabe')}
            </Card.Title>
            <Card.Body>
              <Container className="p-0">
                <Row>
                  <BestaetigenForm
                    update={update}
                    formData={vaxEditData}
                    edit={true}
                  />
                </Row>
              </Container>
            </Card.Body>
          </SquareShadowCard>
        </div>
        <div
          className="position-sticky"
          style={{
            bottom: '0',
            backgroundColor: '#f6f8fb',
            padding: '20px',
            zIndex: 1201
          }}
        >
          <div className="d-flex justify-content-between">
            {vaxEditData.edited && (
              <Button
                onClick={restoreData}
                variant={'warning'}
                size="lg"
                className="d-flex justify-content-center align-items-center"
              >
                <Undo />
                <div className="ms-2">{t('nutzerbereich.eintraege.bearbeiten.verwerfen')}</div>
              </Button>
            )}
            {!vaxEditData.edited && (
              <LoadingButton
                onClick={deleteEntry}
                loading={isLoadingDelete}
                variant="contained"
                color="error"
                size="lg"
                className="d-flex justify-content-center align-items-center"
              >
                <DeleteForever />
                <div className="ms-2">{t('nutzerbereich.eintraege.bearbeiten.loeschen')}</div>
              </LoadingButton>
            )}
            <Button
              onClick={back}
              variant={'outline-secondary'}
              size="lg"
              className="d-flex justify-content-center align-items-center"
            >
              <ArrowBack />
              <div className="ms-2">{t('nutzerbereich.eintraege.bearbeiten.zurueck_zur_uebersicht')}</div>
            </Button>
            <LoadingButton
              disabled={!vaxEditData.edited}
              onClick={saveEntry}
              loading={isLoadingSave}
              variant="contained"
              size="lg"
              className="d-flex justify-content-center align-items-center"
            >
              <Save />
              <div className="ms-2">{t('common.speichern')}</div>
            </LoadingButton>
            {user?.is_moderator && (
              <LoadingButton
                disabled={vaxEditData.edited}
                onClick={publishEntry}
                loading={isLoadingPublish}
                variant="contained"
                color="success"
                size="lg"
                className="d-flex justify-content-center align-items-center"
              >
                <Publish />
                <div className="ms-2">{t('nutzerbereich.eintraege.bearbeiten.veroeffentlichen')}</div>
              </LoadingButton>
            )}
          </div>
        </div>
      </div>
      <Modal
        show={showAdminRestriction}
        onHide={handleClose}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('common.bitte_vom_admin_pruefen_lassen')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(newItems == 'newVaxTypes' || newItems == 'both') &&
            t('common.dieser_eintrag_enthaelt_eine_neue_impfung') + ' '}
          {(newItems == 'newManufacturers' || newItems == 'both') &&
            t('common.dieser_eintrag_enthält_einen_neuen_impfstoffhersteller') + ' '}
          {newItems && t('common.wende_dich_an_deinen_leiter')}
        </Modal.Body>
      </Modal>
      <Modal
        show={showAdminWarning}
        onHide={handleClose}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('common.neue_impfung')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(newItems == 'newVaxTypes' || newItems == 'both') &&
            t('common.dieser_eintrag_enthaelt_eine_neue_impfung') + ' '}
          {(newItems == 'newManufacturers' || newItems == 'both') &&
            t('common.dieser_eintrag_enthält_einen_neuen_impfstoffhersteller') + ' '}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between align-items-center justify-items-between w-100 gap-4">
            <LoadingButton
              onClick={handleClose}
              variant="contained"
              color="error"
              size="lg"
            >
              {t('common.nein')}
            </LoadingButton>
            <LoadingButton
              onClick={acceptNewTypes}
              variant="contained"
              color="success"
              size="lg"
              className="grow-0"
            >
              {t('common.ja_hinzufuegen_und_veroeffentlichen')}
            </LoadingButton>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ImpfschadenBearbeiten;
